/**
 * Shared shell for blog articles: encapsulates the typography styles,
 * lang-aware body switching and lead-magnet rendering.
 *
 * Usage:
 *   <ArticleShell
 *     bodyEs={<BodyEs />}
 *     bodyCa={<BodyCa />}
 *     leadMagnet={{
 *       guide: "...",
 *       source: "...",
 *       title: { es: "...", ca: "..." },
 *       subtitle: { es: "...", ca: "..." },
 *       bullets: { es: [...], ca: [...] },
 *     }}
 *   />
 */
import LeadMagnet from "@/components/site/LeadMagnet";
import { useLang } from "@/lib/i18n";

const ARTICLE_STYLE = `
  .article-body { font-family: 'DM Sans', sans-serif; color: #2D2C2A; }
  .article-body h2 { font-family: 'Cormorant Garamond', serif; font-size: 2rem; line-height: 1.15; margin-top: 2.6rem; margin-bottom: 1rem; color: #2D2C2A; font-weight: 500; }
  .article-body h3 { font-family: 'Cormorant Garamond', serif; font-size: 1.45rem; line-height: 1.2; margin-top: 1.8rem; margin-bottom: 0.6rem; color: #2D2C2A; font-weight: 500; }
  .article-body p { font-size: 17px; line-height: 1.75; margin-bottom: 1.1rem; color: #2D2C2A; opacity: 0.85; }
  .article-body strong { color: #2D2C2A; font-weight: 600; opacity: 1; }
  .article-body em { color: #2D2C2A; opacity: 1; }
  .article-body ul, .article-body ol { margin: 1rem 0 1.4rem; padding-left: 1.4rem; }
  .article-body li { margin-bottom: 0.55rem; line-height: 1.7; color: #2D2C2A; opacity: 0.85; }
  .article-body blockquote { border-left: 3px solid #D4A373; padding: 0.5rem 1.4rem; margin: 1.8rem 0; font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 1.4rem; line-height: 1.45; color: #2D2C2A; }
`;

const pick = (val, lang) => {
  if (val && typeof val === "object" && !Array.isArray(val)) return val[lang] ?? val.es;
  return val;
};

const ArticleShell = ({ bodyEs, bodyCa, leadMagnet }) => {
  const lang = useLang();
  return (
    <div className="prose prose-charcoal max-w-none">
      <style>{ARTICLE_STYLE}</style>
      {lang === "ca" ? bodyCa : bodyEs}
      {leadMagnet ? (
        <LeadMagnet
          guide={leadMagnet.guide}
          source={leadMagnet.source}
          title={pick(leadMagnet.title, lang)}
          subtitle={pick(leadMagnet.subtitle, lang)}
          bullets={pick(leadMagnet.bullets, lang)}
        />
      ) : null}
    </div>
  );
};

export default ArticleShell;
